import { parseInt, range, uniqueId } from "lodash";
import { transparentize } from "polished";
import React from "react";
import styled, { useTheme } from "styled-components";

export type RadarChartProps = {
  values: Record<
    string,
    {
      value: number;
      max: number;
    }
  >;
};

const Container = styled.svg`
  max-width: 100%;
`;

const Dot = styled.circle`
  fill: ${(props) => props.theme.colors.primary};
`;

const Web = styled.path`
  stroke: ${(props) => props.theme.colors.primary};
  stroke-width: 3;
`;

const Axis = styled.line`
  stroke: ${(props) => props.theme.colors.grey3};
  stroke-width: 2;
`;

const Ring = styled.path`
  stroke: ${(props) => props.theme.colors.grey5};
  stroke-width: 2;
  fill: ${(props) => transparentize(0.7, props.theme.colors.grey5)};
`;

const Label = styled.text`
font-family ${(props) => props.theme.fonts.body};
font-size: 18px;
fill: ${(props) => props.theme.colors.primary};
font-weight: 600;
text-shadow: 0px 0px 3px rgba(0,0,0,0.2);
`;

const buildPath = (positions: { x: number; y: number }[]): string =>
  `M ${positions[0].x} ${positions[0].y} L ${positions
    .slice(1)
    .map(({ x, y }) => `${x} ${y}`)
    .join(" ")} Z`;

export const RadarChart = ({ values }: RadarChartProps) => {
  const maxGivenValue = Math.max(...Object.values(values).map((x) => x.value));

  const cx = 500;
  const cy = 500;
  const margin = 250;

  const dotPosition = (index: number, value: number, max: number) => {
    if (!max) {
      max = maxGivenValue;
    }

    const perAngle = (Math.PI * 2) / Object.values(values).length;
    const angle = index * perAngle;
    const length = (value / max) * (cx - margin);

    return {
      x: cx + Math.sin(angle) * length,
      y: cy + Math.cos(angle) * length,
    };
  };

  const positions = Object.values(values).map((x, i) =>
    dotPosition(i, x.value, x.max),
  );

  const axisPositions = positions.map((_, i) => dotPosition(i, 1, 1));

  const rings = range(5).map((x) =>
    positions.map((_, i) => dotPosition(i, x / 4, 1)),
  );

  const labels = Object.keys(values).map((x, i) => ({
    label: x,
    ...dotPosition(i, 1.1, 1),
  }));

  const theme = useTheme();

  const id = uniqueId();

  return (
    <Container viewBox={`0 220 1000 600`}>
      <defs>
        <radialGradient id={`radargrad${id}`}>
          <stop
            offset="10%"
            stopColor={transparentize(0.9, theme.colors.primary)}
          />
          <stop
            offset="95%"
            stopColor={transparentize(0.6, theme.colors.primary)}
          />
        </radialGradient>
      </defs>
      {labels.map((x) => (
        <Label
          x={x.x}
          y={x.y}
          textAnchor={
            Math.round(x.x) === cx ? "middle" : x.x > cx ? "start" : "end"
          }
          dominantBaseline={
            Math.round(x.y) === cy
              ? "middle"
              : x.y < cy
                ? "hanging"
                : "alphabetic"
          }
        >
          {x.label.replace(/_/g, " ")}
        </Label>
      ))}
      {axisPositions.map((x) => (
        <Axis x1={cx} y1={cy} x2={x.x} y2={x.y} />
      ))}
      {rings.map((x) => (
        <Ring d={buildPath(x)} />
      ))}
      <Web d={buildPath(positions)} fill={`url('#radargrad${id}')`} />
      {positions.map((p) => (
        <Dot cx={p.x} cy={p.y} r={10} />
      ))}
    </Container>
  );
};
